import React, { useContext, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import Box from '@mui/system/Box'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import ReceiptExpenseForm from '../component/ReceiptExpenseForm'
import MileageExpenseForm from '../component/MileageExpenseForm'
import { postNewExpense } from 'services/api/modules/claim/expense'
import { getAllCategoriesWithBalanceAllocation } from 'services/api/modules/claim/category'
import { Typography } from '@mui/material'
import { ExpenseContext } from '../context/ExpenseContext'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxButton from 'components/custom/AdxButton'

const NewExpenseForm = ({
  reportData,
  reportId,
  handleClose,
  setModalOpen,
  setInfoModalOpen,
  selectedCategory,
  setSelectedCategory,
}) => {
  // call data from API and filter only active categories
  const handleSetSelectCategory = (event) => {
    setSelectedCategory(JSON.parse(event.target.value))
  }

  const refForm = useRef('form')

  // get all category API
  const {
    isLoading: categoryIsLoading,
    error: categoryError,
    data: categoryData,
  } = useQuery('categoryData', async () => {
    const { data } = await getAllCategoriesWithBalanceAllocation()
    return data
  })

  // post new expense API
  const queryClient = useQueryClient()

  const {
    isLoading: expenseLoading,
    error: expenseError,
    mutate: expenseMutate,
  } = useMutation((values) => postNewExpense(reportId, values), {
    onSuccess: async () => {
      await queryClient.refetchQueries(['singleReportData'], { active: true })
      await queryClient.invalidateQueries('singleReportData')
      await queryClient.invalidateQueries('categoryData')
      setExpenseSession({})
      setModalOpen(false)
      setInfoModalOpen(true)
    },
    onError: async (error) => {
      if (error?.status === 422) {
        refForm?.current?.setErrors({
          amount: error?.data?.errors?.amount ? error?.data?.errors?.amount[0] : null,
          attachment: error?.data?.errors?.attachment ? error?.data?.errors?.attachment[0] : null,
          proof_link: error?.data?.errors?.proof_link
            ? error?.data?.errors?.proof_link[0]
            : null,
        })
      } else if (error?.status === 404) {
        refForm?.current?.setErrors({
          calculatedAmount: error?.data?.message,
        })
      } else {
        console.log(error)
      }
    },
  })

  // filtered category
  const generalCategory = categoryData?.categories?.filter((item) => item.claim_type === 'General')
  const mileageCategory = categoryData?.categories?.filter((item) => item.claim_type === 'Mileage')

  useEffect(() => {
    refForm.current.setFieldValue('claim_expense_category_id', selectedCategory?.id)
  }, [selectedCategory])

  // 1. resume info modal states and function
  const [resumeMode, setResumeMode] = useState(false)
  const [isResumeModalOpen, setResumeModalOpen] = useState(false)
  const handleResumeOpen = () => {
    setResumeModalOpen(true)
  }
  const handleResumeClose = () => {
    setResumeModalOpen(false)
  }

  // 2. the modal will trigger if there is expense description in the expense context
  // check context
  const { savedExpenseSession, setExpenseSession } = useContext(ExpenseContext)

  useEffect(() => {
    if (savedExpenseSession?.description) {
      handleResumeOpen()
    }
  }, [])

  // 3. handlers when  button at resume modal is clicked
  const handleResume = () => {
    setResumeMode(true)
    setExpenseSession({ ...savedExpenseSession, resumeStatus: true })
    handleResumeClose()
  }
  const handleCancelResume = () => {
    setExpenseSession({})
    handleResumeClose()
  }

  // 4. handle behaviour of dropdown option and filter the categoryData from API which has the similar id with savedExpenseSession?.claim_expense_category_id
  const handleDefaultValue = () => {
    if (resumeMode) {
      const resumeCategory = categoryData?.categories?.filter(
        (category) => category.id === savedExpenseSession?.claim_expense_category_id
      )
      return JSON?.stringify(...resumeCategory)
    } else {
      return JSON?.stringify(selectedCategory)
    }
  }

  // 5. trigger rerendering if changes occured in resumeMode state to update the dropdown category state and form field value
  useEffect(() => {
    if (resumeMode) {
      handleDefaultValue()
      setSelectedCategory(JSON?.parse(handleDefaultValue()))
      setTimeout(() => {
        refForm?.current?.setValues({
          ...savedExpenseSession,
        })
      }, 0)
    }
  }, [resumeMode])

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AdxDropdown
          label="Category"
          value={handleDefaultValue()}
          onChange={handleSetSelectCategory}
          menuPropsSx={{
            '& .MuiMenuItem-root': {
              backgroundColor: '#ffffff !important',           
            },
            '& .MuiMenuItem-root:hover': {
              backgroundColor: '#fdf6e2 !important',  
            },
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: '#fdf6e2 !important', 
            },
          }}
        >
          {generalCategory?.length > 0 && (
            <Typography variant="body2" paddingY={1} paddingX={2} color="light-gray-text.main">
              GENERAL
            </Typography>
          )}
          {generalCategory?.map((item, index) => (
            <AdxMenuItem
              selectedvalue={JSON.stringify(selectedCategory)}
              key={index}
              value={JSON.stringify(item)}
              sx={{ width: 'calc(100% - 32px)', marginLeft: 2 }}
              disabled={item.allocated === 0}
            >
              {item.name}
            </AdxMenuItem>
          ))}
          {mileageCategory?.length > 0 && (
            <Typography variant="body2" paddingY={1} paddingX={2} color="light-gray-text.main">
              MILEAGE
            </Typography>
          )}
          {mileageCategory?.map((item, index) => (
            <AdxMenuItem
              selectedvalue={JSON.stringify(selectedCategory)}
              key={index}
              value={JSON.stringify(item)}
              sx={{ width: 'calc(100% - 32px)', marginLeft: 2 }}
              disabled={item.allocated === 0}
            >
              {item.name}
            </AdxMenuItem>
          ))}
        </AdxDropdown>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedCategory?.claim_type === 'Mileage' ? (
            <MileageExpenseForm
              reportId={reportId}
              reportData={reportData}
              category={selectedCategory?.id}
              disabled={!selectedCategory ? true : false}
              ref={refForm}
              mileageRate={selectedCategory?.rate}
              categoryAllocation={selectedCategory?.allocated}
              expenseMutate={expenseMutate}
            />
          ) : (
            <ReceiptExpenseForm
              reportData={reportData}
              reportId={reportId}
              category={selectedCategory?.id}
              disabled={!selectedCategory ? true : false}
              ref={refForm}
              categoryAllocation={selectedCategory?.allocated}
              expenseMutate={expenseMutate}
            />
          )}

          <Box
            width={1}
            display="flex"
            flexDirection={{ xs: 'column-reverse', lg: 'row' }}
            justifyContent="center"
            alignContent="center"
            columnGap={2}
            sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', lg: 'row' }, gap: 1 }}
          >
            <AdxButton
              variant={'outlined'}
              onClick={handleClose}
              sx={{
                width: { xs: 1, lg: 1 / 5 },
              }}
            >
              Cancel
            </AdxButton>
            <AdxButton
              variant={'contained'}
              isLoading={expenseLoading}
              disabled={!selectedCategory || expenseLoading}
              sx={{
                width: { xs: 1, lg: 4 / 5 },
              }}
              onClick={() => {
                refForm.current.handleSubmit()
              }}
            >
              Add expense
            </AdxButton>
          </Box>
        </Box>
      </Box>

      {/* modal to resume */}
      <AdxInfoModal
        variant="standard"
        open={isResumeModalOpen}
        disableAutoFocus
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleResumeClose()
          }
        }}
        handleClose={handleResumeClose}
        title="Resume previous expenses"
        subtitle="We've detected a previous unsubmitted expenses. Do you want to continue your previous session?"
        footerButtons
        firstBtnLabel="Resume"
        firstBtnHandle={handleResume}
        secondBtnLabel="Cancel"
        secondBtnHandle={handleCancelResume}
      ></AdxInfoModal>
    </>
  )
}

export default NewExpenseForm
